import { downloadSheet, postForm, postJson } from "./http";

export interface TallyChartLedger {
  name: string;
  /** Stable id. Custom groups and ledgers use this; reserved Tally groups are not rows. */
  account_id: string;
  /** Set only when the parent is a stored custom group. Null when the parent is a reserved group. */
  parent_id: string | null;
  account_type: string | null;
  parent_name: string | null;
  is_group: boolean;
  destination: string;
  guid: string | null;
}

export interface TallyChart {
  company_name: string | null;
  ledgers: TallyChartLedger[];
  banks: TallyChartLedger[];
}

export interface TallyLedgerIssue {
  field: string;
  message: string;
}

export interface TallyLedgerImportRow {
  id: number;
  sheet_row_no: number;
  kind: string;
  account_name: string;
  account_type: string | null;
  parent_name: string | null;
  action: "new" | "unchanged" | "invalid";
  status: "pending" | "queued" | "created" | "skipped" | "failed";
  issues: TallyLedgerIssue[];
  error: string | null;
  tally_ledger_guid: string | null;
}

export interface TallyLedgerImport {
  import: {
    id: number;
    source: string;
    filename: string | null;
    status: string;
    total_rows: number;
    new_rows: number;
    unchanged_rows: number;
    invalid_rows: number;
    created_rows: number;
    failed_rows: number;
    duplicate_of_commit: boolean;
  } | null;
  rows: TallyLedgerImportRow[];
}

export interface TallyNoBillRow {
  account_name: string;
  label: string;
  is_parent: boolean;
  action: string;
  status: string;
  error: string | null;
}

export interface TallyNoBillState {
  parent: { name: string; exists: boolean; guid: string | null };
  existing: { name: string; guid: string | null }[];
  candidates: { name: string; guid: string | null }[];
  mapped: { name: string; guid: string }[];
  import: { status: string; created_rows: number; failed_rows: number; new_rows: number } | null;
  rows: TallyNoBillRow[];
}

interface DataEnvelope<T> {
  data: T;
}

const chart = (publicId: string) =>
  postJson<DataEnvelope<TallyChart>>("/api/onboarding/tally/ledgers/chart", { public_id: publicId }).then((r) => r.data);

const template = (publicId: string) =>
  downloadSheet("/api/onboarding/tally/ledgers/template", { public_id: publicId }, "chart-of-accounts.xlsx");

const upload = async (publicId: string, file: File): Promise<TallyLedgerImport> => {
  const form = new FormData();
  form.append("public_id", publicId);
  form.append("file", file);
  return (await postForm<DataEnvelope<TallyLedgerImport>>("/api/onboarding/tally/ledgers/import/upload", form, ["file"])).data;
};

const currentImport = (publicId: string) =>
  postJson<DataEnvelope<TallyLedgerImport>>("/api/onboarding/tally/ledgers/import/get", { public_id: publicId }).then((r) => r.data);

const cancelImport = (publicId: string) =>
  postJson("/api/onboarding/tally/ledgers/import/cancel", { public_id: publicId });

const commitImport = (publicId: string) =>
  postJson<DataEnvelope<TallyLedgerImport>>("/api/onboarding/tally/ledgers/import/commit", { public_id: publicId }).then((r) => r.data);

const closeImport = (publicId: string) =>
  postJson("/api/onboarding/tally/ledgers/import/close", { public_id: publicId });

const noBill = (publicId: string) =>
  postJson<DataEnvelope<TallyNoBillState>>("/api/onboarding/tally/ledgers/no-bill/get", { public_id: publicId }).then((r) => r.data);

const saveNoBillMapping = (publicId: string, guids: string[]) =>
  postJson<DataEnvelope<TallyNoBillState>>("/api/onboarding/tally/ledgers/no-bill/mapping", { public_id: publicId, guids }).then((r) => r.data);

const stageNoBill = (publicId: string, labels: string[]) =>
  postJson<DataEnvelope<TallyNoBillState>>("/api/onboarding/tally/ledgers/no-bill/stage", { public_id: publicId, labels }).then((r) => r.data);

const commitNoBill = (publicId: string) =>
  postJson<DataEnvelope<TallyNoBillState>>("/api/onboarding/tally/ledgers/no-bill/commit", { public_id: publicId }).then((r) => r.data);

const cancelNoBill = (publicId: string) =>
  postJson<DataEnvelope<TallyNoBillState>>("/api/onboarding/tally/ledgers/no-bill/cancel", { public_id: publicId }).then((r) => r.data);

const closeNoBill = (publicId: string) =>
  postJson<DataEnvelope<TallyNoBillState>>("/api/onboarding/tally/ledgers/no-bill/close", { public_id: publicId }).then((r) => r.data);

export const tallyLedgerApi = {
  chart,
  template,
  upload,
  currentImport,
  cancelImport,
  commitImport,
  closeImport,
  noBill,
  saveNoBillMapping,
  stageNoBill,
  commitNoBill,
  cancelNoBill,
  closeNoBill,
};
